import { createContext, useCallback, useContext, useMemo, useRef } from 'react';

import type { ReactNode } from 'react';

export type Event = {
  action: 'inView' | 'click' | 'telemetry';
  scope?: string;
  page?: string;
  target: string;
  props: Record<string, string>;
};

type Logger = (eventName: string, data?: Record<string, unknown>) => void;

// 這裡的 context 更像是一個 namespace，後面會建立多個「Provider」做使用
const EventServiceContext = createContext({} as Partial<Event>);

const EventLoggerContext = createContext((() => {
  console.warn("warning: haven't implement the logger");
}) as Logger);

// 用在頂層，讓整個 service 使用的 provider
export const EventServiceProvider = ({
  children,
  logger,
}: {
  children: ReactNode;
  logger: Logger;
}) => {
  return (
    <EventLoggerContext.Provider value={logger}>
      <EventServiceContext.Provider value={{}}>
        {children}
      </EventServiceContext.Provider>
    </EventLoggerContext.Provider>
  );
};
/**
 * merge: 父層 + 子層的合併規則：
 *
 * 一般欄位：後者覆蓋前者（{...a, ...b}）
 * props：做淺合併，且 b.props 覆蓋 a.props 同名 key
 */
function merge(a: Partial<Event> = {}, b: Partial<Event> = {}) {
  const props = { ...(a?.props ?? {}), ...(b?.props ?? {}) };

  return { ...a, ...b, ...{ props } };
}

// 比較給的 scope 中的 event 有沒有變化，如果沒有變化的話，就 memo 值
function shallowEqualEvent(a: Partial<Event>, b: Partial<Event>) {
  // 如果是同一個 reference，不檢查直接視為相同
  if (a === b) return true;

  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (const key of aKeys) {
    if (!Object.prototype.hasOwnProperty.call(b, key)) return false;
    const propsKey = key as keyof Event;

    if (a[propsKey] !== b[propsKey]) return false;
  }
  return true;
}

export function useShallowStableEvent(event: Partial<Event>) {
  // 幫從 props 來的值作 patch 穩定化，讓外部可以直接透過 props={{...}} 來送值
  const ref = useRef(event);
  if (!shallowEqualEvent(ref.current, event)) ref.current = event;
  return ref.current;
}

export const EventScope = ({
  eventProps,
  children,
}: {
  children: ReactNode;
  eventProps: Partial<Event>;
}) => {
  const parentProps = useContext(EventServiceContext);

  const stabledEventProps = useShallowStableEvent(eventProps);

  const scopedEventProps = useMemo(
    () => merge(parentProps, stabledEventProps),
    [parentProps, stabledEventProps],
  );

  return (
    <EventServiceContext.Provider value={scopedEventProps}>
      {children}
    </EventServiceContext.Provider>
  );
};

const Page = ({
  page,
  children,
  props = {},
}: {
  children: ReactNode;
  page: string;
  props?: Event['props'];
}) => <EventScope eventProps={{ page, props }}>{children}</EventScope>;

EventScope.Page = Page;

const makeEventName = (event: Partial<Event>) => {
  const eventAction = event?.action ?? '';

  const ACTION_NAME_MAP: Record<string, string> = {
    click: '點擊',
    inView: 'pageview',
    telemetry: 'telemetry',
  };

  const actionName = eventAction ? (ACTION_NAME_MAP[eventAction] ?? '') : '';
  const name = [event.page, event.scope, event.target, actionName]
    .filter((e) => !!e)
    .join('_');

  return name;
};

export const useLogEvent = (
  action: Event['action'],
  target: Event['target'],
) => {
  const logger = useContext(EventLoggerContext);
  const eventService = useContext(EventServiceContext);

  // 這邊要有個 create Event 的邏輯
  const handleLogEvent = useCallback(
    (props?: Event['props']) => {
      const event = merge(eventService, {
        action,
        target,
        props,
      });

      const name = makeEventName(event);

      const eventProp =
        Object.keys(event?.props ?? {}).length < 1 ? undefined : event.props;

      logger(name, eventProp);

      return name;
    },
    [action, eventService, target],
  );

  return handleLogEvent;
};
